/* eslint-disable no-undef */
import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Router } from 'react-router-dom';
import { dataDogInit } from 'helpers/dataDog';
import qs from 'query-string';
import { CDN_TYPE_SPORT, getCDNUrl, LocalConfig } from 'utils';

import {
  AccountManager,
  AnalyticsManager,
  ApiManager,
  Atlas,
  BetManager,
  Bus,
  ExtensionsManager,
  initDebug,
  isNotEmptyObject,
  Logger,
  SettingsManager,
  SiteConfigManager,
  SportManager,
  TranslationProvider,
  UserMessagesProvider,
} from '@atlas/core';
import { CLIENT_PROFILE_COMPONENT_TYPES, COMPONENT_TYPES, staticRoutes } from '@atlas/routing';
import { TransportManager } from '@atlas/transport';

import Notifications from 'common/Notifications';
import { NotificationsUIProvider } from 'common/Popup/NotificationsUIContext';
import ToastContainer from 'common/Toast/ToastContainer';
import {
  syncRequestBuilderAuthFromApiManager,
  syncRequestBuilderDefaultHeaders,
} from 'common/utils/requestBuilder';
import ForgotPassword from 'components/Auth/ForgotPassword';
import ResetPassword from 'components/Auth/ResetPassword';
import SignIn from 'components/Auth/SignIn';
import SignUp from 'components/Auth/SignUp';
import BlogRouter from 'components/Blog/BlogRouter';
import ClientProfile from 'components/ClientProfile/ClientProfile';
import CmsPageList from 'components/CmsPageList/CmsPageList';
import ContactSupportTeamContainer from 'components/ContactSupportTeamContainer/ContactSupportTeamContainer';
import Layout from 'components/Layout/Layout';
import LiveChatContainer from 'components/LiveChat/LiveChatContainer';
import LuckyDay from 'components/LuckyDay/LuckyDay';
import history from 'components/MainRouter/history';
import MainRouter from 'components/MainRouter/MainRouter';
import GlobalMaintenanceContainer from 'components/MaintenanceContainer/GlobalMaintenanceContainer';
import Casino from 'components/Pages/Casino/Casino';
import Live from 'components/Pages/Live';
import Menu from 'components/Pages/Menu';
import Settings from 'components/Pages/Menu/SettingsMenuItem/Settings';
import Sportsbook from 'components/Pages/Prematch';
import ResultMain from 'components/Pages/Results';
import PromotionsRouter from 'components/Promotion/PromotionsRouter';
import { SearchInputProvider } from 'components/Search/context/InputProvider';
import SeoPageProvider from 'components/SeoPage/SeoPageProvider';
import TemporaryPopupToPageRedirect from 'components/TemporaryPopupToPageRedirect/TemporaryPopupToPageRedirect';
import SmarticoTournamentsRouter from 'components/Tournament/Smartico/TournamentsRouter';
import TournamentRouter from 'components/Tournament/TournamentRouter';
import TVGames from 'components/TVGames/TVGames';
import VirtualGame from 'components/VirtualGame/VirtualGame';
import BonusesProvider from 'src/components/BonusesProvider';
import DebugInfo from 'src/components/DebugInfo/DebugInfo';
import { LayoutLoader } from 'src/components/Layout/LayoutLoader';
import { OddsFormatProvider } from 'src/components/Pages/Menu/OddsFormatSettings/OddsFormatProvider';
import SelfRegulationProvider from 'src/components/SelfRegulationProvider';
import subscribeUserOffline from 'src/subscriptions/subscribeUserOffline';

import 'common/Form/formConfig/formConfig';
import '../subscriptions/dataLayer.bindings';

import packageData from '../../package.json';
import resolveFingerprint from '../fingerprint';
import busEventsMapping from '../subscriptions/bus.bindings';

const ShareCss = lazy(() => import('modules/share-css'));
const AllProviders = lazy(() => import('components/AllProviders/AllProviders'));

// Some casino providers (e.g. Playtech) navigate their game iframe to the lobbyUrl we pass, which
// loads the site inside the game frame ("lobby in lobby"). The site build is never meant to run
// framed — the dedicated iframe build sets window.IS_IFRAME — so break out to the top window.
const isFramedSiteBuild = window.top && window.top !== window.self && !window.IS_IFRAME;

if (isFramedSiteBuild) {
  try {
    window.top.location.replace(window.location.pathname);
  } catch (error) {
    Logger.debug('iframe break-out skipped', error);
  }
}

dataDogInit();
initDebug();

const SuspenseCssComponent = () => (
  <Suspense fallback={<div />}>
    <ShareCss />
  </Suspense>
);

// Updating application default settings using local config
const localLanguage = LocalConfig.get('language');
if (localLanguage) {
  SettingsManager.settings.lang = localLanguage;
}

SiteConfigManager.device = 'mobile';

// Layouts setup
SiteConfigManager.setLayoutConfig({
  [COMPONENT_TYPES.LUCKY_DAY]: {
    component: LuckyDay,
    defaultPath: staticRoutes.luckyday,
  },
  [COMPONENT_TYPES.SPORT_LIVE]: {
    component: Live,
    path: staticRoutes.sportLive,
  },
  [COMPONENT_TYPES.SPORT_PREMATCH]: {
    component: Sportsbook,
    path: staticRoutes.sportPrematch,
  },
  [COMPONENT_TYPES.RESULTS]: {
    component: ResultMain,
    path: staticRoutes.results,
  },
  [COMPONENT_TYPES.CASINO]: {
    component: Casino,
    path: staticRoutes.casino,
  },
  [COMPONENT_TYPES.MENU]: {
    component: Menu,
    path: staticRoutes.menu,
  },
  [COMPONENT_TYPES.CLIENT_PROFILE]: {
    component: ClientProfile,
    path: staticRoutes.account,
  },
  [CLIENT_PROFILE_COMPONENT_TYPES.SETTINGS]: {
    component: Settings,
    path: staticRoutes.settings,
  },
  [COMPONENT_TYPES.TV_GAMES]: {
    component: TVGames,
    path: staticRoutes.tvGames,
  },
  [COMPONENT_TYPES.VIRTUAL_GAME]: {
    component: VirtualGame,
    path: staticRoutes.virtualGame,
  },
  [COMPONENT_TYPES.CASINO_LIVE]: {
    component: Casino,
    path: staticRoutes.casinoLive,
  },
  [COMPONENT_TYPES.PROMOTIONS]: {
    component: PromotionsRouter,
    optionalPath: '/:id?',
    path: staticRoutes.promotions,
  },
  [COMPONENT_TYPES.BLOG]: {
    component: BlogRouter,
    optionalPath: '/:id?',
    path: staticRoutes.blog,
  },
  [COMPONENT_TYPES.PROMOTION_TOURNAMENTS]: {
    component: TournamentRouter,
    defaultPath: staticRoutes.promotionTournaments,
    optionalPath: '/:id?',
  },
  [COMPONENT_TYPES.TOURNAMENTS]: {
    component: SmarticoTournamentsRouter,
    defaultPath: staticRoutes.tournaments,
    optionalPath: '/:id?',
  },
  forgotPassword: {
    component: ForgotPassword,
    path: staticRoutes.forgotPassword,
  },
  pages: {
    component: CmsPageList,
    defaultPath: '/pages',
  },
  resetPassword: {
    component: ResetPassword,
    path: staticRoutes.resetPassword,
  },
  signin: {
    component: SignIn,
    path: staticRoutes.signin,
  },
  signup: {
    component: SignUp,
    path: staticRoutes.signup,
  },
  [COMPONENT_TYPES.All_PROVIDERS]: {
    component: AllProviders,
    path: staticRoutes.allProvidersPage,
  },
});

const providedQueryParams = qs.parse(window.location.search);

// Only for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const localCmsSettings = require('./cms.settings.js');
  SiteConfigManager.setConfig(localCmsSettings.default);

  // Loading svg sprites from dyeing
  fetch('/dyeing/build/sprite/sprite.svg')
    .then(res => res.text())
    .then(svg => {
      document.body.insertAdjacentHTML('beforeend', svg);
    });
}

// Only for production
if (process.env.NODE_ENV === 'production') {
  // Applying default client application settings
  if (isNotEmptyObject(window.CMS_CONFIG)) {
    const { navigation = {}, page = {} } = window.CMS_CONFIG;

    SiteConfigManager.setConfig({ ...page, navigation });
  }
}

// Applying CMS betslip settings
const betslipCMSConfig = SiteConfigManager.getConfig('config.config.betslip');
if (betslipCMSConfig) {
  BetManager.updateBetslipSettings(betslipCMSConfig);
}

const defaultAcceptOddChanges = SiteConfigManager.getConfig(
  'config.config.betslip.defaultAcceptOddsChangesType',
);
if (defaultAcceptOddChanges) {
  SettingsManager.settings.acceptOddsChanges = defaultAcceptOddChanges;
}

const defaultOddsZeroes = SiteConfigManager.getConfig('config.config.odds_zeroes');
if (defaultOddsZeroes) {
  SettingsManager.settings.oddsZeroes = defaultOddsZeroes;
}

const defaultOddsFractionDigits = SiteConfigManager.getConfig('config.config.odds_fraction_digits');
if (defaultOddsFractionDigits) {
  SettingsManager.settings.oddsFractionDigits = defaultOddsFractionDigits;
}

const mobileGridViewType = SiteConfigManager.getConfig(
  'config.config.sportsbook.mobileGridViewType',
);
if (mobileGridViewType === 'simplified') {
  SettingsManager.settings.showAllSports = true;
  SettingsManager.settings.resetFilter = {};
}

// Resolving stored settings
SettingsManager.resolveSettings();

busEventsMapping(Bus, history);
subscribeUserOffline();

const { host, protocol } = window.location;
const webSocketProtocol = protocol.startsWith('https') ? 'wss:' : 'ws:';

ApiManager.setHostname('core', `${protocol}//${host}/api/`);
ApiManager.setHostname('cms', `${protocol}//${host}/api/cms-go/v1/site/`);
ApiManager.setHostname('cms/v2', `${protocol}//${host}/api/cms-go/v2/site/`);
ApiManager.setHostname('ws', `${webSocketProtocol}//${host}/ws/notifier2`);
ApiManager.setHostname('media', 'http://app4.front-server.atlas-iac.com');
ApiManager.setHostname('cdn', getCDNUrl(CDN_TYPE_SPORT));

// The reset password page also has a token in query params thus it causes unexpected behavior.
// It should be removed after the reset password token will be renamed(https://atlaslive.atlassian.net/browse/FT-5672)
const isResetPassword =
  history.location.pathname === staticRoutes.resetPassword ||
  providedQueryParams.cmd === 'resetPassword';
if (providedQueryParams.token && !isResetPassword) {
  ApiManager.setAuthorizationToken(providedQueryParams.token);
  AccountManager.setToken(providedQueryParams.token);
}

if (process.env.NODE_ENV === 'development') {
  window.$sport = SportManager;
  window.BetManager = BetManager;
  window.SiteConfigManager = SiteConfigManager;
  window.Bus = Bus;
  window.TransportManager = TransportManager.getInstance();
}

// Set Auth Token for API service
ApiManager.setAuthorizationToken(AccountManager.getToken());

// Initializing api default headers
const defaultHeaders = {
  device: 'mobile',
  version: packageData.version,
};
const projectId = LocalConfig.get('projectId');
// Providing project id if it exist in config
if (projectId) {
  defaultHeaders['x-project-id'] = projectId;
}

// Providing header for local testing
if (process.env.NODE_ENV === 'development') {
  defaultHeaders['x-project-team'] = 'frontend-team';
}

ApiManager.setDefaultHeaders(defaultHeaders);
syncRequestBuilderDefaultHeaders(defaultHeaders);
syncRequestBuilderAuthFromApiManager(ApiManager);

if (!SettingsManager.settings.theme) {
  Logger.debug(`[settings] setting default theme=${SYS_CONFIG.defaultTheme}`);
  SettingsManager.updateSettings({ theme: SYS_CONFIG.defaultTheme });
}

// Creating atlas extensions manager
window.aem = new ExtensionsManager();

const wsUrl = new URL(ApiManager.getHostname('ws'));
wsUrl.searchParams.set('projectId', projectId);
if (process.env.NODE_ENV === 'development') {
  // Added for security reason, special WAF rule created on Cloudflare
  wsUrl.searchParams.set('project-team-frontend-team', 'x');
}
const transport = TransportManager.getInstance();
transport.initialize(wsUrl);
transport.setOptions({
  locale: localLanguage,
  projectId,
});

// Initializing analytics
AnalyticsManager.init({
  fingerprint: () =>
    resolveFingerprint().then(components => window.btoa(JSON.stringify(components))),
});

const domNode = document.getElementById('root');
const root = createRoot(domNode);

root.render(
  <>
    <SuspenseCssComponent />
    <Suspense fallback={<div />}>
      <Atlas>
        <TranslationProvider>
          <GlobalMaintenanceContainer>
            <ContactSupportTeamContainer>
              <LiveChatContainer>
                <Router history={history}>
                  <LayoutLoader>
                    <SeoPageProvider>
                      <NotificationsUIProvider>
                        <UserMessagesProvider>
                          <SearchInputProvider>
                            <SelfRegulationProvider>
                              <BonusesProvider>
                                <OddsFormatProvider>
                                  <Layout>
                                    <MainRouter />
                                  </Layout>
                                  <DebugInfo withNav />
                                </OddsFormatProvider>
                              </BonusesProvider>
                            </SelfRegulationProvider>
                          </SearchInputProvider>
                        </UserMessagesProvider>
                        <ToastContainer />
                        <Notifications />
                        <TemporaryPopupToPageRedirect />
                      </NotificationsUIProvider>
                    </SeoPageProvider>
                  </LayoutLoader>
                </Router>
              </LiveChatContainer>
            </ContactSupportTeamContainer>
          </GlobalMaintenanceContainer>
        </TranslationProvider>
      </Atlas>
    </Suspense>
  </>,
  document.getElementById('root'),
);
